import type { ValueEvent } from '@adornis/base/utilTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import { getStammdatenByUserAndInstitution } from '@adornis/buchungsheld-db/api/stammdaten.js';
import type { RegistrationProductEntryRequirementsPlugin } from '@adornis/buchungsheld-db/db-plugins.js';
import { ProductEntryRequirement } from '@adornis/buchungsheld-db/entities/ProductEntryRequirement.js';
import { html, type TemplateResult } from 'lit';
import { IcsrmGenderEnum, IcsrmStammdaten } from '../stammdaten/icsrm-stammdaten-plugin.js';
import { defaultRegistrationProductEntryRequirementsPlugin } from './product-entry-requirements-default.js';

@Entity()
export class ProductEntryRequirementForIcsrmGender extends ProductEntryRequirement {
  public static override _class = 'ProductEntryRequirementForIcsrmGender';

  @Field(type => IcsrmGenderEnum, { default: v => v ?? (IcsrmGenderEnum.ICSRM_FEMALE as any) })
  requiredGender?: IcsrmGenderEnum;

  override hasValue() {
    return !!this.requiredGender;
  }

  override renderEditUI({ tr }): TemplateResult<1> {
    const selectables = [IcsrmGenderEnum.ICSRM_FEMALE, IcsrmGenderEnum.ICSRM_MALE, IcsrmGenderEnum.ICSRM_DIVERSE];
    return html`
      <tr-dropdown-selection
        .value=${this.requiredGender}
        .selectables=${selectables}
        .renderString=${(gender: IcsrmGenderEnum) => tr.translate('enums', gender)}
        @value-picked=${(e: ValueEvent<IcsrmGenderEnum>) => (this.requiredGender = e.detail.value)}
      ></tr-dropdown-selection>
    `;
  }

  override async requirementIsMet(userID: string) {
    const stammdaten = await getStammdatenByUserAndInstitution<IcsrmStammdaten>(
      userID,
      this.institutionID,
    )({ gender: 1 });
    if (!stammdaten)
      throw new Error('could not find stammdaten for user=' + userID + ', institution=' + this.institutionID);
    return stammdaten.gender === this.requiredGender;
  }

  override async label() {
    if (!this.requiredGender || this.requiredGender === IcsrmGenderEnum.ICSRM_NOT_SPECIFIED)
      return ['---', '---'] satisfies [string, string];
    if (this.requiredGender === IcsrmGenderEnum.ICSRM_FEMALE)
      return ['nur für Frauen', 'women only'] satisfies [string, string];
    if (this.requiredGender === IcsrmGenderEnum.ICSRM_MALE)
      return ['nur für Männer', 'men only'] satisfies [string, string];
    if (this.requiredGender === IcsrmGenderEnum.ICSRM_DIVERSE)
      return ['nur für nicht binär', 'non-binary only'] satisfies [string, string];
    throw new Error('gender not handled: ' + this.requiredGender);
  }
}

export const icsrmRegistrationProductEntryRequirementsPlugin: RegistrationProductEntryRequirementsPlugin = {
  id: 'registration::product-entry-requirements::icsrm',
  type: 'registration::product-entry-requirements',
  getRequirementKinds() {
    return [
      ...defaultRegistrationProductEntryRequirementsPlugin.getRequirementKinds(),
      ProductEntryRequirementForIcsrmGender,
    ];
  },
};
